import type {
  ApiError,
  AdminIncidentUpdatePayload,
  AdminIncidentUpdateResponse,
  AdminIncidentResponse,
  AdminLoginResponse,
  AdminMaintenanceResponse,
  AdminMeResponse,
  AdminServicesResponse,
  AdminIncidentsResponse,
  AdminMaintenanceResponseList,
  IncidentStatus,
  IncidentSeverity,
  PublicIncidentDetailResponse,
  PublicIncidentsResponse,
  PublicMaintenanceResponse,
  PublicServicesResponse,
  PublicStatusResponse,
  Service,
  UserRole,
} from "@statuspage/shared";

export type { ApiError };

export interface AuthUser {
  id: string;
  tenant_id: string;
  email: string;
  role: UserRole;
  created_at: string;
}

export interface LoginPayload {
  tenantId: string;
  email: string;
  password: string;
}

export interface AdminServiceUpdatePayload {
  name?: string;
  url?: string;
  method?: string;
  check_interval_seconds?: number;
  expected_status?: number;
  keyword_match?: string | null;
  timeout_ms?: number;
  is_active?: number;
  display_order?: number;
  group_name?: string | null;
}

export interface AdminServiceCreatePayload {
  name: string;
  url: string;
  method: string;
  check_interval_seconds: number;
  expected_status: number;
  keyword_match: string | null;
  timeout_ms: number;
  is_active: number;
  display_order: number;
  group_name: string | null;
}

export interface IncidentUpdatePayload {
  status?: IncidentStatus;
  body?: string;
  severity?: IncidentSeverity;
}

export interface IncidentCreatePayload {
  service_id?: string;
  title: string;
  status: IncidentStatus;
  severity: IncidentSeverity;
  body: string | null;
}

export interface MaintenanceCreatePayload {
  service_id?: string;
  title: string;
  description: string | null;
  scheduled_at: string;
  duration_min: number;
}

export interface ApiFailure extends ApiError {
  code?: string;
}

export class ApiRequestError extends Error {
  public readonly status: number;
  public readonly code?: string | undefined;

  public constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = "ApiRequestError";
    this.status = status;
    if (code !== undefined) {
      this.code = code;
    }
  }
}

export interface FetchOptions<T> {
  method?: "GET" | "POST" | "PUT" | "DELETE";
  body?: T;
  signal?: AbortSignal;
  auth?: boolean;
}

const AUTH_TOKEN_KEY = "statuspage_admin_token";
const UNAUTHORIZED_EVENT = "statuspage:auth:unauthorized";

function normalizeApiBaseUrl(value: string | undefined): string {
  const trimmed = value?.trim() ?? "";
  return trimmed.replace(/\/+$/, "");
}

const API_BASE_URL = normalizeApiBaseUrl(import.meta.env.VITE_API_BASE_URL);

export function buildApiUrl(path: string): string {
  if (/^https?:\/\//i.test(path)) {
    return path;
  }

  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return API_BASE_URL ? `${API_BASE_URL}${normalizedPath}` : normalizedPath;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function parseJsonSafe(response: Response): Promise<unknown> {
  return response.json().catch(() => ({}));
}

function normalizeErrorPayload(payload: unknown): ApiFailure {
  if (isRecord(payload) && typeof payload.error === "string") {
    const base: ApiFailure = {
      error: payload.error,
      ...(typeof payload.code === "string" ? { code: payload.code } : {}),
    };
    return base;
  }

  return { error: "Request failed", code: "REQUEST_ERROR" };
}

function notifyUnauthorized(): void {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(UNAUTHORIZED_EVENT));
  }
}

export function getStoredToken(): string | null {
  if (typeof window === "undefined") return null;
  return sessionStorage.getItem(AUTH_TOKEN_KEY);
}

export function setStoredToken(token: string | null): void {
  if (typeof window === "undefined") return;
  if (!token) {
    sessionStorage.removeItem(AUTH_TOKEN_KEY);
    return;
  }
  sessionStorage.setItem(AUTH_TOKEN_KEY, token);
}

function authHeaders(): HeadersInit {
  const token = getStoredToken();
  if (!token) return {};
  return {
    Authorization: `Bearer ${token}`,
  };
}

async function requestJson<T>(path: string, options: FetchOptions<unknown> = {}): Promise<T> {
  const headers: HeadersInit = {
    Accept: "application/json",
    ...(options.body ? { "Content-Type": "application/json" } : {}),
  };

  if (options.auth) {
    const authHeader = authHeaders();
    Object.assign(headers, authHeader);
  }

  const init: RequestInit = {
    method: options.method ?? "GET",
    headers,
  };

  const initWithSignal = options.signal ? { ...init, signal: options.signal } : init;

  if (options.body) {
    initWithSignal.body = JSON.stringify(options.body);
  }

  const response = await fetch(buildApiUrl(path), initWithSignal);

  if (response.status === 401) {
    notifyUnauthorized();
  }

  if (!response.ok) {
    const errorPayload = normalizeErrorPayload(await parseJsonSafe(response));
    throw new ApiRequestError(errorPayload.error, response.status, errorPayload.code);
  }

  const data = await parseJsonSafe(response);
  return data as T;
}

export async function fetchStatus(tenant: string, signal?: AbortSignal): Promise<PublicStatusResponse> {
  const options: FetchOptions<unknown> = { method: "GET" };
  if (signal) {
    options.signal = signal;
  }
  return requestJson<PublicStatusResponse>(`/api/${tenant}/status`, options);
}

export async function fetchAdminServices(): Promise<AdminServicesResponse> {
  return requestJson<AdminServicesResponse>("/api/admin/services", {
    method: "GET",
    auth: true,
  });
}

export async function fetchAdminIncidents(): Promise<AdminIncidentsResponse> {
  return requestJson<AdminIncidentsResponse>("/api/admin/incidents", {
    method: "GET",
    auth: true,
  });
}

export async function fetchAdminMaintenance(): Promise<AdminMaintenanceResponseList> {
  return requestJson<AdminMaintenanceResponseList>("/api/admin/maintenance", {
    method: "GET",
    auth: true,
  });
}

export async function fetchPublicServices(tenant: string, options?: { limit?: number; offset?: number }): Promise<PublicServicesResponse> {
  const limit = options?.limit ?? 50;
  const offset = options?.offset ?? 0;
  return requestJson<PublicServicesResponse>(`/api/${tenant}/services?limit=${limit}&offset=${offset}`, {
    method: "GET",
  });
}

export async function fetchPublicIncidents(tenant: string, options?: { limit?: number; offset?: number }): Promise<PublicIncidentsResponse> {
  const limit = options?.limit ?? 50;
  const offset = options?.offset ?? 0;
  return requestJson<PublicIncidentsResponse>(`/api/${tenant}/incidents?limit=${limit}&offset=${offset}`, {
    method: "GET",
  });
}

export async function fetchPublicIncidentDetail(tenant: string, incidentId: string): Promise<PublicIncidentDetailResponse> {
  return requestJson<PublicIncidentDetailResponse>(`/api/${tenant}/incidents/${incidentId}`, {
    method: "GET",
  });
}

export async function fetchPublicMaintenance(tenant: string, options?: { limit?: number; offset?: number }): Promise<PublicMaintenanceResponse> {
  const limit = options?.limit ?? 50;
  const offset = options?.offset ?? 0;
  return requestJson<PublicMaintenanceResponse>(`/api/${tenant}/maintenance?limit=${limit}&offset=${offset}`, {
    method: "GET",
  });
}

export async function loginAdmin(payload: LoginPayload): Promise<AdminLoginResponse> {
  return requestJson<AdminLoginResponse>("/api/auth/login", {
    method: "POST",
    body: payload,
    auth: false,
  });
}

export async function fetchAdminMe(): Promise<AdminMeResponse> {
  return requestJson<AdminMeResponse>("/api/admin/me", {
    method: "GET",
    auth: true,
  });
}

export async function adminCreateService(payload: AdminServiceCreatePayload): Promise<{ service: Service }> {
  return requestJson<{ service: Service }>("/api/admin/services", {
    method: "POST",
    body: payload,
    auth: true,
  });
}

export async function adminUpdateService(serviceId: string, payload: AdminServiceUpdatePayload): Promise<{ service: Service }> {
  return requestJson<{ service: Service }>(`/api/admin/services/${serviceId}`, {
    method: "PUT",
    body: payload,
    auth: true,
  });
}

export async function adminDeleteService(serviceId: string): Promise<{ message: string }> {
  return requestJson<{ message: string }>(`/api/admin/services/${serviceId}`, {
    method: "DELETE",
    auth: true,
  });
}

export async function adminCreateIncident(payload: IncidentCreatePayload): Promise<AdminIncidentResponse> {
  return requestJson<AdminIncidentResponse>("/api/admin/incidents", {
    method: "POST",
    body: payload,
    auth: true,
  });
}

export async function adminUpdateIncident(incidentId: string, payload: IncidentUpdatePayload): Promise<AdminIncidentResponse> {
  return requestJson<AdminIncidentResponse>(`/api/admin/incidents/${incidentId}`, {
    method: "PUT",
    body: payload,
    auth: true,
  });
}

export async function adminDeleteIncident(incidentId: string): Promise<{ message: string }> {
  return requestJson<{ message: string }>(`/api/admin/incidents/${incidentId}`, {
    method: "DELETE",
    auth: true,
  });
}

export async function adminCreateIncidentUpdate(incidentId: string, payload: AdminIncidentUpdatePayload): Promise<AdminIncidentUpdateResponse> {
  return requestJson<AdminIncidentUpdateResponse>(`/api/admin/incidents/${incidentId}/updates`, {
    method: "POST",
    body: payload,
    auth: true,
  });
}

export async function adminCreateMaintenance(payload: MaintenanceCreatePayload): Promise<AdminMaintenanceResponse> {
  return requestJson<AdminMaintenanceResponse>("/api/admin/maintenance", {
    method: "POST",
    body: payload,
    auth: true,
  });
}

export async function adminDeleteMaintenance(maintenanceId: string): Promise<{ message: string }> {
  return requestJson<{ message: string }>(`/api/admin/maintenance/${maintenanceId}`, {
    method: "DELETE",
    auth: true,
  });
}

export async function adminTriggerChecks(): Promise<{ message: string; summary: { ok: boolean; reason?: string } }> {
  return requestJson<{ message: string; summary: { ok: boolean; reason?: string } }>("/api/admin/checks/trigger", {
    method: "POST",
    auth: true,
  });
}

export { UNAUTHORIZED_EVENT };
